import { RefObject } from 'react';
import { Range, RangeKeyDict } from 'react-date-range';

import { IdLabelValue } from '@types';

import Checkbox from '../checkbox/Checkbox';
import SearchBar from '../searchbar/SearchBar';
import Spinner from '../spinner/Spinner';
import OptionItem from './OptionItem';
import { FieldOptionV2, SelectedFilterValues } from './types';

interface OptionsPanelProps {
  currentField?: FieldOptionV2;
  activeField: string;
  isSingleSelect: boolean;
  hasOptions: boolean;
  isSelectAllMode: boolean;
  isAllSelected: boolean;
  selected: SelectedFilterValues;
  excludedItems: SelectedFilterValues;
  searchTerms: Record<string, string>;
  dateRange: Range;
  optionsScrollRef: RefObject<HTMLDivElement>;
  onSearch: (term: string) => void;
  onScroll: () => void;
  onToggleItem: (item: IdLabelValue, checked: boolean) => void;
  onToggleSelectAll: (checked: boolean) => void;
  onDateChange: (obj: RangeKeyDict) => void;
  onSelectedChange: (field: string, values: string[]) => void;
}

const OptionsPanel = ({
  currentField,
  activeField,
  isSingleSelect,
  hasOptions,
  isSelectAllMode,
  isAllSelected,
  selected,
  excludedItems,
  searchTerms,
  dateRange,
  optionsScrollRef,
  onSearch,
  onScroll,
  onToggleItem,
  onToggleSelectAll,
  onDateChange,
  onSelectedChange
}: OptionsPanelProps) => {
  if (currentField?.panelType === 'custom' && currentField.CustomPanel) {
    const { CustomPanel } = currentField;
    return (
      <div className="flex flex-1 flex-col border-l border-cofeeNeutral4">
        <CustomPanel
          value={selected[activeField] || []}
          onChange={(values) => onSelectedChange(activeField, values)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col border-l border-cofeeNeutral4 py-2">
      {currentField?.hasSearch && (
        <div className="shrink-0 px-4 pb-3 pt-1.5">
          <SearchBar
            placeholder="Search"
            value={searchTerms[activeField] || ''}
            onSearch={onSearch}
            searchBarStyle="w-full !h-9"
            debounceDelay={500}
          />
        </div>
      )}

      <div
        ref={optionsScrollRef}
        onScroll={onScroll}
        className="custom-scroll flex-1 overflow-y-auto overscroll-contain pl-4"
      >
        {!isSingleSelect &&
          hasOptions &&
          (currentField?.options?.length ?? 0) > 1 && (
            <div
              className="cursor-pointer rounded-md px-2 py-2.5"
              role="presentation"
              onClick={() => onToggleSelectAll(!isAllSelected)}
            >
              <Checkbox
                handleChange={(e) => onToggleSelectAll(e.target.checked)}
                label="Select all"
                isChecked={isAllSelected}
                labelStyle="!text-sm !font-normal !leading-none !tracking-normal !text-textblack"
              />
            </div>
          )}

        {hasOptions ? (
          <>
            {currentField?.options?.map((item) => {
              const isItemSelected = isSelectAllMode
                ? !(excludedItems[activeField]?.includes(item.value) ?? false)
                : selected[activeField]?.includes(item.value) ?? false;
              return (
                <OptionItem
                  key={item.id}
                  item={item}
                  isSingleSelect={isSingleSelect}
                  isSelected={isItemSelected}
                  onToggle={onToggleItem}
                  dateRange={dateRange}
                  onDateChange={onDateChange}
                />
              );
            })}
            {currentField?.hasPagination && currentField?.isLoading && (
              <div className="flex justify-center py-2">
                <Spinner size="sm" />
              </div>
            )}
          </>
        ) : (
          <div className="flex h-40 items-center justify-center text-sm text-grey">
            {currentField?.isLoading ? (
              <Spinner size="sm" />
            ) : (
              'No results found'
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OptionsPanel;
